// src/components/FatturazioneDashboardWidget.js
// Riquadro Dashboard "Commesse da verificare per fatturazione" — compare solo negli ultimi
// 3 giorni lavorativi del mese (weekend e festività italiane escluse automaticamente).
import { RigaRiepilogo } from "./SuggerimentiOperativiWidget";
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import RichiestaFatturazioneModal from "./RichiestaFatturazioneModal";
import { numIt } from "../utils/economiaCommessa";
import { useVedeTutteFatturazioni } from "../hooks/useCanSee";
import { percentualeRichiestaCommessa } from "../utils/fatturazioneCommessa";

function pad2(n) { return String(n).padStart(2, "0"); }

function calcolaPasqua(anno) {
  const a = anno % 19, b = Math.floor(anno/100), c = anno % 100;
  const d = Math.floor(b/4), e = b % 4, f = Math.floor((b+8)/25);
  const g = Math.floor((b-f+1)/3), h = (19*a+b-d-g+15) % 30;
  const i = Math.floor(c/4), k = c % 4, l = (32+2*e+2*i-h-k) % 7;
  const m = Math.floor((a+11*h+22*l)/451);
  const mese = Math.floor((h+l-7*m+114)/31), giorno = ((h+l-7*m+114) % 31) + 1;
  return new Date(anno, mese-1, giorno);
}

function festivitaItaliane(anno) {
  const pasqua = calcolaPasqua(anno);
  const pasquetta = new Date(pasqua); pasquetta.setDate(pasqua.getDate()+1);
  const fmt = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  return new Set([
    `${anno}-01-01`, `${anno}-01-06`, fmt(pasqua), fmt(pasquetta),
    `${anno}-04-25`, `${anno}-05-01`, `${anno}-06-02`, `${anno}-08-15`,
    `${anno}-11-01`, `${anno}-12-08`, `${anno}-12-25`, `${anno}-12-26`,
  ]);
}

function siamoNegliUltimiGiorniLavorativiDelMese(oggi, nGiorni = 3) {
  const anno = oggi.getFullYear();
  const festivi = festivitaItaliane(anno);
  const ultimoGiornoMese = new Date(anno, oggi.getMonth()+1, 0);
  const ds = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  let trovati = 0;
  let d = new Date(ultimoGiornoMese);
  while (trovati < nGiorni) {
    const isWeekend = d.getDay()===0 || d.getDay()===6;
    const isFestivo = festivi.has(ds(d));
    if (!isWeekend && !isFestivo) trovati++;
    if (trovati === nGiorni) break;
    d.setDate(d.getDate()-1);
  }
  const inizioFinestra = d;
  const oggiStr = ds(oggi);
  return oggiStr >= ds(inizioFinestra) && oggiStr <= ds(ultimoGiornoMese);
}

export default function FatturazioneDashboardWidget({ currentUser, isOrgAdmin, isPlatformOwner, effectiveOrgId, onNavigate, compatto = false }) {
  const mostraWidget = siamoNegliUltimiGiorniLavorativiDelMese(new Date());

  const [projects, setProjects] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [budget, setBudget] = useState([]);
  const [progettoSelezionato, setProgettoSelezionato] = useState(null);

  useEffect(() => {
    if (!mostraWidget || !effectiveOrgId) return;
    return onSnapshot(query(collection(db,"projects"), where("organizationId","==",effectiveOrgId), where("statoCommessa","==","attiva")),
      snap => setProjects(snap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(p=>!p.isParent && !p.senzaCliente && !p.archived)));
  }, [effectiveOrgId, mostraWidget]);

  // Richieste di fatturazione: tutte per chi le gestisce, le proprie per il PM (regole 57).
  // La percentuale della commessa per il PM viene dai totali salvati sul budget.
  const vedeTutte = useVedeTutteFatturazioni();
  useEffect(() => {
    if (!mostraWidget || !effectiveOrgId || vedeTutte === null || !currentUser?.uid) return;
    const filtri = [where("organizationId","==",effectiveOrgId)];
    if (!vedeTutte) filtri.push(where("richiedenteUid","==",currentUser.uid));
    return onSnapshot(query(collection(db,"richiesteFatturazione"), ...filtri),
      snap => setRichieste(snap.docs.map(d=>({id:d.id,...d.data()}))), () => setRichieste([]));
  }, [effectiveOrgId, mostraWidget, vedeTutte, currentUser?.uid]);

  useEffect(() => {
    if (!mostraWidget || !effectiveOrgId) return;
    // Filtro per organizzazione: senza, le Regole Firestore rifiutano la lettura e il widget
    // restava senza importi
    return onSnapshot(query(collection(db,"budgetCommessa"), where("organizationId","==",effectiveOrgId)),
      snap => setBudget(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [effectiveOrgId, mostraWidget]);

  if (!mostraWidget) return null;

  const isAdmin = isOrgAdmin || isPlatformOwner;
  const commesseVisibili = isAdmin ? projects : projects.filter(p => p.responsabileUid === currentUser?.uid);

  const righe = commesseVisibili.map(p => {
    const bud = budget.find(b => b.projectId===p.id);
    const percFatturato = percentualeRichiestaCommessa(p.id, richieste, bud, vedeTutte);
    const importoCommessa = bud && numIt(bud.importoCommessa)>0 ? numIt(bud.importoCommessa) : null;
    return { project:p, percFatturato, importoCommessa };
  });

  const verdi = righe.filter(r => r.percFatturato >= 99.99);
  const gialli = righe.filter(r => r.percFatturato > 0 && r.percFatturato < 99.99);
  const rossi = righe.filter(r => r.percFatturato <= 0);

  if (righe.length === 0) return null;

  // Home (ottobre 2026): una riga sola; l'elenco completo è nel Centro Operativo
  if (compatto) {
    const daVerificare = gialli.length + rossi.length;
    return <RigaRiepilogo icona="📄" colore={daVerificare ? "#d97706" : "#059669"}
      testo={daVerificare ? `Fine mese: ${daVerificare} commess${daVerificare === 1 ? "a" : "e"} da verificare per la fatturazione` : "Fine mese: commesse tutte fatturate"}
      onApri={() => onNavigate && onNavigate({ page: "control-center" })}/>;
  }

  const Riga = ({ r, colore }) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
      <div style={{minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {r.project.codiceCommessa?`[${r.project.codiceCommessa}] `:""}{r.project.name}
        </div>
        <div style={{fontSize:11,color:"#9ca3af"}}>
          {r.project.clienteNome||"—"} · {r.project.responsabileNome||"—"}
          {colore!=="verde" && ` · richiesto in fattura ${(r.percFatturato).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}% · residuo ${((100-r.percFatturato)).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%`}
        </div>
      </div>
      {colore==="verde" ? (
        <span style={{fontSize:11,fontWeight:700,color:"#059669",flexShrink:0,marginLeft:10}}>100%</span>
      ) : (
        <button onClick={()=>setProgettoSelezionato(r.project)}
          style={{fontSize:11,fontWeight:700,color:"#7c3aed",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:7,padding:"5px 10px",cursor:"pointer",flexShrink:0,marginLeft:10,whiteSpace:"nowrap"}}>
          📄 Richiedi Fatturazione
        </button>
      )}
    </div>
  );

  return (
    <div style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:12,padding:20,marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
        <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
          📄 Commesse da verificare per fatturazione
        </h3>
        <button onClick={()=>onNavigate && onNavigate({page:"da-fatturare"})}
          style={{fontSize:11,fontWeight:600,color:"#7c3aed",background:"none",border:"none",cursor:"pointer"}}>
          Richieste di fatturazione →
        </button>
      </div>
      <p style={{fontSize:11,color:"#9ca3af",marginBottom:14}}>Ultimi giorni lavorativi del mese — controlla se qualcosa è pronto per la fatturazione.</p>

      <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
        <span style={{fontSize:12,color:"#6b7280"}}>Commesse aperte: <strong style={{color:"#111827"}}>{righe.length}</strong></span>
        <span style={{fontSize:12,color:"#6b7280"}}>🟢 Fatturate: <strong style={{color:"#059669"}}>{verdi.length}</strong></span>
        <span style={{fontSize:12,color:"#6b7280"}}>🟡 Parziali: <strong style={{color:"#d97706"}}>{gialli.length}</strong></span>
        <span style={{fontSize:12,color:"#6b7280"}}>🔴 Mai inviate: <strong style={{color:"#dc2626"}}>{rossi.length}</strong></span>
      </div>

      {rossi.length>0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:"#dc2626",marginBottom:4}}>🔴 Mai inviate in fatturazione</div>
          {rossi.map(r=><Riga key={r.project.id} r={r} colore="rosso"/>)}
        </div>
      )}
      {gialli.length>0 && (
        <div style={{marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:"#d97706",marginBottom:4}}>🟡 Fatturazione parziale</div>
          {gialli.map(r=><Riga key={r.project.id} r={r} colore="giallo"/>)}
        </div>
      )}
      {verdi.length>0 && (
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"#059669",marginBottom:4}}>🟢 Completamente fatturate</div>
          {verdi.map(r=><Riga key={r.project.id} r={r} colore="verde"/>)}
        </div>
      )}

      {progettoSelezionato && (
        <RichiestaFatturazioneModal project={progettoSelezionato} orgId={effectiveOrgId} currentUser={currentUser}
          onClose={()=>setProgettoSelezionato(null)}/>
      )}
    </div>
  );
}
